<template>
  <div class="field-mapper">
    <div v-if="localSourceFields.length > 0" class="columns">
      <div class="table-container">
        <div class="source-column">
          <h4>Source Fields</h4>
          <div
            v-for="(field, index) in localSourceFields"
            :key="field"
            class="field-item"
          >
            <button
              type="button"
              :class="getButtonClass(field, 'source')"
              @click="selectField(field, 'source', index)"
            >
              {{ field }}
            </button>
          </div>
        </div>

        <div class="target-column">
          <h4>Target Fields</h4>
          <div
            v-for="field in orderedTargetFields"
            :key="field.name"
            class="field-item"
          >
            <button
              type="button"
              :class="getButtonClass(field.name, 'target')"
              @click="selectField(field.name, 'target')"
            >
              {{ field.name }}
            </button>
            <span v-if="field.order !== null">Order: {{ field.order }}</span>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: "FieldMapper",
  props: {
    sourceFields: {
      type: Array,
      required: true,
    },
    targetFields: {
      type: Array,
      required: true,
    },
  },
  data() {
    return {
      localSourceFields: [],
      localTargetFields: [],
      selectedSourceField: null,
      selectedSourceIndex: null,
      selectedTargetField: null,
      mappings: {}, // { sourceField: targetField }
    };
  },
  computed: {
    orderedTargetFields() {
      // sort by order, unmapped at end (order === null)
      return this.localTargetFields
        .slice()
        .sort((a, b) => {
          const ao = a.order === null ? Number.POSITIVE_INFINITY : a.order;
          const bo = b.order === null ? Number.POSITIVE_INFINITY : b.order;
          return ao - bo;
        });
    },
  },
  watch: {
    // If parent updates source/target props (e.g., after parsing headers), refresh state and emit new order
    sourceFields: {
      handler(newVal) {
        this.localSourceFields = Array.isArray(newVal) ? [...newVal] : [];
        this.reseedTargets();
        this.autoConnectFields();
        this.reorderTargetFields();
        this.emitColumnOrder();
      },
      immediate: true,
    },
    targetFields: {
      handler(newVal) {
        this.reseedTargets(newVal);
        this.autoConnectFields();
        this.reorderTargetFields();
        this.emitColumnOrder();
      },
      immediate: true,
    },
  },
  methods: {
    reseedTargets(list = this.targetFields) {
      this.localTargetFields = (list || []).map((name) => ({ name, order: null }));
      // preserve existing mappings where possible
      const preserved = {};
      Object.entries(this.mappings).forEach(([src, tgt]) => {
        if ((list || []).includes(tgt)) preserved[src] = tgt;
      });
      this.mappings = preserved;
    },

    getButtonClass(field, type) {
      if (type === "source") {
        return this.mappings[field] ? "btn btn-success" : "btn btn-outline-danger";
      }
      if (type === "target") {
        return Object.values(this.mappings).includes(field)
          ? "btn btn-success"
          : "btn btn-outline-danger";
      }
      return "btn";
    },

    selectField(field, type, index = null) {
      if (type === "source") {
        this.selectedSourceField = field;
        this.selectedSourceIndex = index;
        return;
      }

      if (type === "target") {
        this.selectedTargetField = field;

        if (this.selectedSourceField !== null) {
          // map selected source → target
          this.mappings[this.selectedSourceField] = field;

          // set target order == source index
          const targetField = this.localTargetFields.find((f) => f.name === field);
          if (targetField) {
            targetField.order = this.selectedSourceIndex;
          }

          // reset selection and tidy orders
          this.selectedSourceField = null;
          this.selectedSourceIndex = null;
          this.selectedTargetField = null;

          this.resetUnconnectedOrders();
          this.reorderTargetFields();
          this.emitColumnOrder();
        }
      }
    },

    resetUnconnectedOrders() {
      this.localTargetFields.forEach((field) => {
        if (!Object.values(this.mappings).includes(field.name)) {
          field.order = null;
        }
      });
    },

    reorderTargetFields() {
      const used = new Set();
      const unmapped = [];

      // collect used orders and record unmapped
      this.localTargetFields.forEach((f) => {
        if (f.order !== null && f.order !== undefined) {
          used.add(f.order);
        } else {
          unmapped.push(f);
        }
      });

      // fill gaps for unmapped to make a tidy visual order (doesn't affect columnOrder generation)
      let next = 0;
      unmapped.forEach((f) => {
        while (used.has(next)) next++;
        f.order = next;
        used.add(next);
        next++;
      });
    },

    autoConnectFields() {
      // naive normalised match (strip spaces, lower-case)
      this.localSourceFields.forEach((sourceField, index) => {
        const ns = (sourceField || "").replace(/\s+/g, "").toLowerCase();
        const match = this.localTargetFields.find(
          (t) => (t.name || "").replace(/\s+/g, "").toLowerCase() === ns
        );
        if (match) {
          this.mappings[sourceField] = match.name;
          match.order = index;
        }
      });
    },

    // Build and emit the column order for the parent to store
    emitColumnOrder() {
      const columnOrder = this.localSourceFields
        .map((src) => this.mappings[src] || "")
        .filter(Boolean) // drop unmapped
        .join(";");      // semicolon-delimited

      this.$emit("columnOrderGenerated", columnOrder);
    },
  },
  mounted() {
    // initial state already set via watchers (immediate: true)
    // ensure we emit once on mount
    this.emitColumnOrder();
  },
};
</script>

<style scoped>
.field-mapper {
  display: flex;
  flex-direction: column;
  align-items: center;
}

.columns {
  display: flex;
  justify-content: center;
  width: 50%;
}

.table-container {
  display: flex;
  justify-content: space-between;
  width: 100%;
  border: 1px solid #ccc;
  padding: 10px;
  box-sizing: border-box;
}

.source-column, .target-column {
  width: 50%;
  display: flex;
  flex-direction: column;
}

.source-column .field-item, .target-column .field-item {
  display: flex;
  justify-content: center;
  width: 100%;
}

.field-item {
  margin-bottom: 10px;
  border: 1px solid #ccc;
  padding: 5px;
  box-sizing: border-box;
}

button {
  width: 100%;
  padding: 10px;
}
</style>
